import { config } from '@js/state';

/**
 * EndScene — reward reveal + play-again CTA.
 *
 * Spotlight + reward-reveal sequence is a direct port of
 * `games/buzzer-frizer/src/js/scenes/EndScene.js`:
 *   • dim overlay + endSpotlight multiatlas plays "spotlightOpen" (frames 0-188)
 *   • baseCircle plays "circleopen" then loops "circleglitterloop"
 *   • rewardLight plays "rewardopen" once
 *   • the reward (gold-crate) starts off-centre below, scaled 0.3, angled 300°,
 *     and tweens up to the centre with scale 1, angle 0
 *   • glitter sprite orbits the reward in an elliptical curve
 * Truck-loading-specific layer on top: "Golden crate" caption + Play-again CTA pill.
 */
let rewardTriggered = false;

export default class EndScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EndScene' });
  }

  create() {
    rewardTriggered = false;
    const { width: W, height: H } = config;

    this.cameras.main.fadeIn(400, 0, 0, 0);

    // Win fanfare — kicks off with the reward reveal.
    if (this.cache.audio.exists('sfx-fanfare')) {
      this.sound.play('sfx-fanfare', { volume: 0.6 });
    }

    // Layering, bottom → top:
    //   1. reward-bg    — Figma 187:84 (sky + cityscape + road + plaza)
    //   2. end-overlay  — dark wash that dims the scene
    //   3. spotlight-cone — gradient cone hanging from top
    //   4. reward elements (added later in runRewardSequence)
    this.cameras.main.setBackgroundColor('#D6F5FF');
    if (this.textures.exists('reward-bg')) {
      const bg = this.add.image(W / 2, H / 2, 'reward-bg');
      const sx = W / (bg.width || 1);
      const sy = H / (bg.height || 1);
      bg.setScale(Math.max(sx, sy));
    }

    if (this.textures.exists('end-overlay')) {
      this.endOverlay = this.add
        .image(W / 2, H / 2, 'end-overlay')
        .setDisplaySize(W, H)
        .setAlpha(0.7);
    }

    if (this.textures.exists('spotlight-cone')) {
      this.spotlightCone = this.add
        .image(W / 2, 0, 'spotlight-cone')
        .setOrigin(0.5, 0)
        .setDisplaySize(720, 717)
        .setAlpha(0);
      this.tweens.add({
        targets: this.spotlightCone,
        alpha: 1,
        duration: 1200,
        delay: 1500,
        ease: 'Sine.easeOut',
      });
    }

    this.buildConfetti(W, H);

    // "Rewards earned!" header sprite slides in from above.
    if (this.textures.exists('rewards-earned')) {
      this.rewardsEarned = this.add
        .image(W / 2, H / 2, 'rewards-earned')
        .setAlpha(0);
      this.tweens.add({
        targets: this.rewardsEarned,
        ease: 'Expo.easeInOut',
        duration: 1000,
        delay: 300,
        alpha: 1,
      });
      this.tweens.add({
        targets: this.rewardsEarned,
        ease: 'Linear',
        duration: 300,
        delay: 1800,
        y: H / 2 - 250,
      });
    }

    // Reward sequence kicks off after the spotlight cone has fully faded
    // in (1500 delay + 1200 duration = 2700ms), giving the stage a beat
    // to establish before the reward rises into the cone.
    this.time.delayedCall(2800, () => this.runRewardSequence());
  }

  buildConfetti(W, H) {
    this.endConfetti = this.add.sprite(W / 2, H / 2, 'endConfetti');
    if (!this.anims.exists('endConfetti')) {
      this.anims.create({
        key: 'endConfetti',
        frames: this.anims.generateFrameNames('endConfetti', {
          start: 0, end: 120, prefix: 'seq_0_', suffix: '.png',
        }),
        frameRate: 40,
        repeat: 0,
      });
    }
  }

  // ─────────────────────── reward reveal sequence ───────────────────────
  runRewardSequence() {
    const W = config.width;
    const H = config.height;
    const midWidth = W / 2;
    const midHeight = H / 2;

    this.yOffset = 75;
    this.yOffsetOfCircle = 85 - 20 - 25; // 40
    this.centerxOfCircle = midWidth;
    this.centeryOfCircle = midHeight - this.yOffsetOfCircle;
    this.rewardAppearDelay = 1300;
    this.rewardMotionDuration = 1350;

    this.baseCircle = this.add
      .sprite(midWidth, midHeight, 'baseCircle')
      .setOrigin(0.5)
      .setScale(0.4);

    this.rewardLight = this.add
      .sprite(midWidth, midHeight, 'baseCircle')
      .setOrigin(0.5)
      .setScale(0.4);

    // The reward — truck-loading uses the gold crate. Starts down-and-rotated
    // and tweens up + un-rotates to land on the spotlight pad. Source PNG is
    // 16× retina (~2120×2012), so we compute a scale that lands on a ~180×170
    // logical display — buzzer-frizer's compass was a tiny 118×152 source so
    // their `scale: 1` was already display-size; ours isn't.
    const REWARD_DISPLAY_W = 180;
    this.reward = this.add
      .image(midWidth, midHeight + 250 - 120, 'gold-crate')
      .setOrigin(0.5)
      .setVisible(false)
      .setAngle(300);
    this.reward.setDisplaySize(
      REWARD_DISPLAY_W,
      REWARD_DISPLAY_W * (this.reward.height / this.reward.width),
    );
    this.rewardTargetScaleX = this.reward.scaleX;
    this.rewardTargetScaleY = this.reward.scaleY;
    this.reward.setScale(this.rewardTargetScaleX * 0.3, this.rewardTargetScaleY * 0.3);
    // (Removed buzzer-frizer's bitmap-mask trick — the masking sprite was
    // rendering as a second dark layer offset upward, creating a "two
    // scenes overlapping" look. The reward's start position is below the
    // spotlight pad, hidden naturally while it rises.)

    this.glitter = this.add
      .sprite(midWidth + 30, midHeight - 65, 'glitter')
      .setOrigin(0.5)
      .setVisible(false)
      .setScale(0.8);

    // Glitter orbit path (elliptical loop around reward).
    this.path = { t: 0, vec: new Phaser.Math.Vector2() };
    this.curve = new Phaser.Curves.Ellipse(
      this.centerxOfCircle,
      this.centeryOfCircle,
      35,
      35,
    );
    this.tweens.add({
      targets: this.path,
      t: 1,
      ease: 'Sine.easeInOut',
      duration: 5400,
      yoyo: true,
      repeat: -1,
    });

    // Reward becomes visible at +1.3s and tweens to the pad.
    this.time.delayedCall(this.rewardAppearDelay, () => {
      this.reward.setVisible(true);
    });
    this.tweens.add({
      targets: this.reward,
      x: midWidth,
      y: midHeight - this.yOffset,
      angle: 0,
      scaleX: this.rewardTargetScaleX,
      scaleY: this.rewardTargetScaleY,
      delay: this.rewardAppearDelay,
      ease: 'Linear',
      duration: 1000,
      onComplete: () => {
        this.glitter.setVisible(true).play('showglitter');
        rewardTriggered = true;

        // Reward bobs up/down (yoyo) — matches the floating sensation in
        // buzzer-frizer's flow.
        this.tweens.add({
          targets: this.curve.p0,
          y: this.centeryOfCircle + 70,
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });
        this.tweens.add({
          targets: this.reward,
          y: { from: midHeight - this.yOffset, to: midHeight + 70 - this.yOffset },
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });

        // Once the reward has landed, slide in the truck-loading caption +
        // CTAs underneath.
        this.buildEndCaption();
        this.buildEndCtas();
      },
    });

    // Animations for the reward atlases.
    if (!this.anims.exists('showglitter')) {
      this.anims.create({
        key: 'showglitter',
        frames: this.anims.generateFrameNames('glitter', {
          start: 0, end: 32, prefix: 'glitterAnim', suffix: '.png',
        }),
        frameRate: 15,
        repeat: -1,
      });
    }
    if (!this.anims.exists('circleopen')) {
      this.anims.create({
        key: 'circleopen',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 0, end: 74, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: 0,
      });
    }
    if (!this.anims.exists('circleglitterloop')) {
      this.anims.create({
        key: 'circleglitterloop',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 74, end: 155, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: -1,
      });
    }
    if (!this.anims.exists('rewardopen')) {
      this.anims.create({
        key: 'rewardopen',
        frames: this.anims.generateFrameNames('rewardLight', {
          start: 0, end: 155, prefix: 'rewardLightAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: 0,
      });
    }

    this.baseCircle.play('circleopen').once('animationcomplete', () => {
      this.baseCircle.play('circleglitterloop');
    });
    this.rewardLight.play('rewardopen');
    this.endConfetti.play('endConfetti');
  }

  update() {
    if (rewardTriggered && this.glitter && this.curve) {
      this.curve.getPoint(this.path.t, this.path.vec);
      this.glitter.setPosition(this.path.vec.x, this.path.vec.y);
    }
  }

  // ─────────────────── caption + CTAs (truck-loading layer) ───────────────────
  buildEndCaption() {
    const W = config.width;
    this.caption = this.add
      .text(W / 2, config.height / 2 + 195, 'GOLDEN CRATE', {
        fontFamily: 'Athletics',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#FFFFFF',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({ targets: this.caption, alpha: 1, duration: 400 });
  }

  buildEndCtas() {
    const W = config.width;
    this.cta = this.buildCtaButton(W / 2, 620, 'btn-play-again', () => this.replay());
    this.cta.bg.setAlpha(0);

    this.tweens.add({
      targets: [this.cta.bg],
      alpha: 1,
      duration: 400,
      delay: 200,
    });
  }

  // ─────────────────────────────── buttons ───────────────────────────────
  buildCtaButton(x, y, key, onClick, opts = {}) {
    // ─── Pre-flight ───
    // Inputs: pointerdown/up on the button sprite.
    // State mutated: button fires onClick once and disables itself.
    // Re-entry guards: disableInteractive() at start of pointerup before firing.
    // Cleanup: sprite destroyed when scene shuts down or modal closed.
    // Manually verified: [x] same-element double click [x] click during scene fade [x] rapid clicks
    const hoverKey = `${key}-hover`;
    const h = opts.height || 48;

    const bg = this.add
      .image(x, y, key)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    // Maintain aspect ratio at the canonical 48px CTA height.
    const w = (bg.width / bg.height) * h;
    bg.setDisplaySize(w, h);

    const hasHoverTexture = this.textures.exists(hoverKey);
    bg.on('pointerover', () => {
      if (hasHoverTexture) bg.setTexture(hoverKey);
      bg.setDisplaySize(w, h);
    });
    bg.on('pointerout', () => {
      bg.setTexture(key);
      bg.setDisplaySize(w, h);
    });
    bg.on('pointerdown', () => bg.setDisplaySize(w * 0.97, h * 0.97));
    bg.on('pointerup', () => {
      bg.disableInteractive();
      bg.setDisplaySize(w, h);
      onClick();
    });

    return { bg };
  }

  // ────────────────────────────── lifecycle ──────────────────────────────
  replay() {
    this.cameras.main.fadeOut(150, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('StartScene');
    });
  }
}
